import { useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const usePDFGenerator = () => {
  const generatePDF = useCallback(async (elementId = 'print-section', filename = 'complaint-record', isAttachement=false) => {
    try {
      const originalZoom = document.body.style.zoom;
      const originalTransform = document.body.style.transform;

      document.body.style.zoom = '1';
      document.body.style.transform = 'scale(1)';

      await new Promise(resolve => setTimeout(resolve, 100));

      const element = document.getElementById(elementId);
      if (!element) {
        console.error(`Element with ID "${elementId}" not found`);
        document.body.style.zoom = originalZoom;
        document.body.style.transform = originalTransform;
        return false;
      }

      const tempStyle = document.createElement('style');
      tempStyle.id = 'pdf-temp-styles';
      tempStyle.innerHTML = `
        #${elementId} * {
          -webkit-font-smoothing: antialiased !important;
          -moz-osx-font-smoothing: grayscale !important;
          text-rendering: optimizeLegibility !important;
          font-kerning: normal !important;
          letter-spacing: 0 !important;
          word-spacing: 0 !important;
          line-height: 1.4 !important;
        }
        #${elementId} .ant-input,
        #${elementId} .ant-select-selection-item,
        #${elementId} textarea {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
        }
        #${elementId} .ant-timeline-item-content {
          min-height: auto !important;
          padding-bottom: 8px !important;
        }
      `;
      document.head.appendChild(tempStyle);

      await new Promise(resolve => setTimeout(resolve, 200));

      // Use bounding rect for exact size
      const rect = element.getBoundingClientRect();

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: rect.width,
        height: rect.height,
        scrollX: 0,
        scrollY: 0,
        letterRendering: true,
        foreignObjectRendering: false, // Try false to fix positioning
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(elementId);
          if (clonedElement) {
            clonedElement.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            clonedElement.style.fontSize = '14px';
            clonedElement.style.lineHeight = '1.4';
            const textElements = clonedElement.querySelectorAll('*');
            textElements.forEach(el => {
              el.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
              el.style.webkitFontSmoothing = 'antialiased';
              el.style.mozOsxFontSmoothing = 'grayscale';
              el.style.textRendering = 'optimizeLegibility';
            });
          }
        }
      });

      const tempStyleElement = document.getElementById('pdf-temp-styles');
      if (tempStyleElement) {
        tempStyleElement.remove();
      }

      document.body.style.zoom = originalZoom;
      document.body.style.transform = originalTransform;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 295;
      const marginX = 10;
      const marginY = 5;

      const imgProps = {
        width: canvas.width,
        height: canvas.height,
      };

      // Calculate width in mm to fit page width minus margins
      const pdfWidth = pageWidth - marginX * 2;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = pdfHeight;
      let position = marginY;

      const imgData = canvas.toDataURL('image/jpeg', 1.0);

      // Add first page
      pdf.addImage(imgData, 'JPEG', marginX, position, pdfWidth, pdfHeight, undefined, 'FAST');
      heightLeft -= (pageHeight - marginY);

      // Add more pages if necessary
      while (heightLeft > 0) {
        position = heightLeft - pdfHeight + marginY;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', marginX, position, pdfWidth, pdfHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      if(isAttachement){
        
        const pdfBlob = pdf.output('blob');
        return pdfBlob

      }else{
        pdf.save(`${filename}_${timestamp}.pdf`);
      }

      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      const tempStyleElement = document.getElementById('pdf-temp-styles');
      if (tempStyleElement) {
        tempStyleElement.remove();
      }
      document.body.style.zoom = '';
      document.body.style.transform = '';
      return false;
    }
  }, []);

  const printPage = useCallback(() => {
    const printStyles = `
      <style id="print-styles">
        @media print {
          body * { visibility: hidden; }
          #print-section, #print-section * { visibility: visible; }
          #print-section {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
          }
          .hr-action-footer { display: none !important; }
          .ant-timeline-item-tail { print-color-adjust: exact; }
        }
      </style>
    `;

    document.head.insertAdjacentHTML('beforeend', printStyles);
    window.print();

    setTimeout(() => {
      const styleElement = document.getElementById('print-styles');
      if (styleElement) {
        styleElement.remove();
      }
    }, 1000);
  }, []);

  return { generatePDF, printPage };
};
